import React from 'react';

import { useIsDesktop } from '@eika/hooks/src/useViewType';
import { Icon, IconTypes } from '@eika/icon';
import { ListPanel } from '@eika/link-panel';

import { CreditCardPanelItem, CreditCardService } from '@/domain/card';
import { useTranslations } from '@/domain/translations';
import useText from '@/hooks/use-text';
import piwik from '@/piwik/piwik';

import { SearchParams, useLinkProducer } from '@domain/app';
import { CreditCard, CreditCardAccount } from '@generated-types';
import { getComplaintUrl } from '@utils/url/get-url-utils';

import './DetailsPanelItems.scss';

type DetailsPanelItemsProps = {
    card: CreditCard;
    account: CreditCardAccount;
};

export const KbDetailsPanelItems = ({ card, account }: DetailsPanelItemsProps) => {
    const produceLink = useLinkProducer();
    const { cardAdmin } = useTranslations();
    const { cardLinkCardControl, cardLinkChangeCreditLimit, documentsTitle, cardLinkComplaint, cardLinkChangeDueDate } =
        useText();
    const isDesktop = useIsDesktop();

    const isOwnerOfCard = CreditCardService.isOwnerOfCard(card);

    return (
        <div className="kb-details-panel-items">
            <h3>{cardAdmin.seeAndChange}</h3>
            <p>{cardAdmin.administeredByKredittbanken}</p>
            <CreditCardPanelItem
                account={account}
                hide={card.status === 'BLOCKED'}
                iconType={IconTypes.JORDEN_24}
                piwikTag={piwik.cardDetails.cardAboutEntryButtonBrukstillatelser}
                sitecorePath="kredittbankenUsagePermissions"
                title={cardLinkCardControl}
                card={card}
            />
            <CreditCardPanelItem
                account={account}
                hide={!isOwnerOfCard}
                iconType={IconTypes.KREDITTGRENSE_24}
                piwikTag={piwik.cardDetails.cardSettingsEntryButtonEndreKredittgrense}
                sitecorePath="kredittbankenChangeCreditLimit"
                title={cardLinkChangeCreditLimit}
                card={card}
            />
            <ListPanel
                prefixContent={<Icon type={IconTypes.BANKKORT_24} />}
                id="link-card-control-block-reorder"
                backgroundColor="light"
                url={produceLink('SELECT_BLOCK_REORDER_PAGE')({
                    [SearchParams.CARD_ID]: card.id,
                    [SearchParams.CARD_TYPE]: 'CREDIT',
                    [SearchParams.ACCOUNT_ID]: account.accountId,
                })}
                icon={IconTypes.PIL_HOEYRE}
                title={cardAdmin.blockReorderTitle}
            />
            <CreditCardPanelItem
                account={account}
                hide={!isOwnerOfCard}
                iconType={IconTypes.FAKTURA_24}
                piwikTag={piwik.cardDetails.cardAboutEntryButtonFakturaOgDokumenter}
                sitecorePath="kredittbankenDocuments"
                title={documentsTitle}
                card={card}
            />
            <CreditCardPanelItem
                account={account}
                hide={!isOwnerOfCard}
                iconType={IconTypes.PLASTER_24}
                piwikTag={piwik.cardDetails.cardAboutEntryButtonForsikringsbevis}
                sitecorePath="kredittbankenInsuranceProof"
                title={cardAdmin.insuranceLinkTitle}
                card={card}
                subtitle={cardAdmin.insuranceLinkSubtitle}
            />
            <CreditCardPanelItem
                hide={!isOwnerOfCard}
                iconType={IconTypes.RADGIVER_24}
                piwikTag={piwik.cardDetails.cardAboutEntryButtonReklamasjon}
                title={cardLinkComplaint}
                id="link-complaint"
                url={getComplaintUrl(produceLink)}
            />
            <CreditCardPanelItem
                account={account}
                card={card}
                hide={!isDesktop}
                iconType={IconTypes.PENN_24}
                piwikTag={piwik.cardDetails.cardAboutEntryButtonChangeDueDate}
                title={cardLinkChangeDueDate}
                id="change-due-date"
                sitecorePath="kredittbankenChangeDueDate"
            />
        </div>
    );
};
